<script>
// As duas pizzas de alocação — hoje × fim da migração (F5, pedido do owner,
// 08/09/2026). Mesmo padrão visual de <donut-chart> do
// `fee-lab-composicao-custo.vue` (chart-data/options no Chart.js 2, legenda
// própria fora do gráfico), simplificado: aqui não há edição nem tabela por
// classe, só o PESO. O cartão do chat não recalcula custo por classe — isso
// depende do fator de cashback do canal (cálculo do motor Python) e não está
// no `S` do chat (ver `utils/alocacao-comparada-do-chat.js`).
//
// ★ "Hoje"/"Depois" são os MESMOS rótulos da tabela do PDF
// (`ff-alocacao.html`, colunas Hoje/Depois/Variação) — número na tela tem
// que bater com número no PDF, e usar o mesmo rótulo é o que deixa a
// comparação óbvia para o consultor.
//
// ★ Sem cor semântica na variação (verde/vermelho): "mais Equity ETF" não é
// bom nem ruim, é só a direção que a migração toma — mesma decisão do PDF
// (`ff-alocacao.html`, comentário da tabela). Cor semântica fica reservada
// ao KPI de custo, que já existe em `FeeLabKpiStrip`.
//
// Traz a PRÓPRIA moldura (`az-card-bordered rounded-sm`, mesmo padrão de
// `FeeLabEvolucaoPatrimonio`) — o consumidor só decide a margem entre blocos
// (`mb-4`), não o card em si.
import { pct } from '@modules/fee-fixo/utils/fee-lab-format'

export default {
  name: 'FeeLabAlocacaoPizzas',
  props: {
    /**
     * Uma linha por classe, na ordem de leitura (peso ALVO decrescente) —
     * o shape de
     * `utils/alocacao-comparada-do-chat.js::alocacaoComparadaDoChat().linhas`.
     * `atual`/`projetada` em FRAÇÃO (0..1), formatadas aqui com `pct()`.
     */
    linhas: {
      type: Array,
      required: true,
    },
  },
  computed: {
    chartDataHoje() {
      return this.construirChartData('atual')
    },
    chartDataDepois() {
      return this.construirChartData('projetada')
    },
    chartOptionsHoje() {
      return this.construirChartOptions('atual')
    },
    chartOptionsDepois() {
      return this.construirChartOptions('projetada')
    },
    // Nenhuma chamada de função no template (regra da casa) — a legenda lê
    // só os rótulos já formatados aqui.
    linhasFormatadas() {
      return this.linhas.map((linha) => ({
        classe: linha.classe,
        nome: linha.nome,
        cor: linha.cor,
        variacaoLabel: pct(linha.atual, 1) + ' → ' + pct(linha.projetada, 1),
      }))
    },
  },
  methods: {
    // Dataset do pie — sem borda (mesmo padrão do donut-chart da composição
    // de custo). Objeto NOVO a cada chamada: o mixin reactiveProp do
    // pie-chart compara referência, não valor — mutar em vez de recriar
    // faria o gráfico não repintar.
    construirChartData(campo) {
      return {
        labels: this.linhas.map((linha) => linha.nome),
        datasets: [
          {
            data: this.linhas.map((linha) => linha[campo] * 100),
            backgroundColor: this.linhas.map((linha) => linha.cor),
            borderWidth: 0,
          },
        ],
      }
    },
    // `options` não é reativo no pie-chart do projeto — o callback do
    // tooltip lê `this.linhas` no momento do hover, nunca um valor
    // capturado na criação do options.
    construirChartOptions(campo) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (tooltipItem) => {
              const linha = this.linhas[tooltipItem.index]
              return linha ? linha.nome + ': ' + pct(linha[campo], 1) : ''
            },
          },
        },
      }
    },
  },
}
</script>

<template>
  <div class="ff-alocacao-pizzas az-card-bordered rounded-sm">
    <p data-teste="simulacao-alocacao-titulo" class="az-ds-label mb-2">
      O que muda na sua carteira
    </p>

    <div
      class="d-flex flex-wrap justify-center align-center az-gap ff-alocacao-pizzas__grafs"
    >
      <div class="d-flex flex-column align-center ff-alocacao-pizzas__graf">
        <p class="az-ds-aux mb-1">Hoje</p>
        <pie-chart
          data-teste="simulacao-alocacao-pizza-hoje"
          :chart-data="chartDataHoje"
          :options="chartOptionsHoje"
          :width="140"
          :height="140"
        />
      </div>
      <div class="d-flex flex-column align-center ff-alocacao-pizzas__graf">
        <p class="az-ds-aux mb-1">Depois</p>
        <pie-chart
          data-teste="simulacao-alocacao-pizza-depois"
          :chart-data="chartDataDepois"
          :options="chartOptionsDepois"
          :width="140"
          :height="140"
        />
      </div>
    </div>

    <ul class="ff-alocacao-pizzas__legenda mt-3">
      <li
        v-for="linha in linhasFormatadas"
        :key="linha.classe"
        data-teste="simulacao-alocacao-linha"
        class="d-flex align-center az-gap-xsmall az-ds-aux ff-alocacao-pizzas__item"
      >
        <span
          class="ff-alocacao-pizzas__cor"
          :style="{ backgroundColor: linha.cor }"
        />
        <span class="ff-alocacao-pizzas__nome">{{ linha.nome }}</span>
        <span>{{ linha.variacaoLabel }}</span>
      </li>
    </ul>
  </div>
</template>

<style lang="sass" scoped>
.ff-alocacao-pizzas
  &__graf
    flex: 0 0 auto

  // Mesma armadilha do eixo das listas dentro do shell (`.content-nav ul`,
  // CLAUDE.md do módulo, §14): sem o eixo declarado a legenda deita e cada
  // item herda 18px de margem.
  &__legenda
    display: flex
    flex-direction: column
    align-items: stretch
    list-style: none
    margin: 0
    padding: 0

  &__item
    margin-right: 0

  &__cor
    width: 8px
    height: 8px
    border-radius: 50%
    flex-shrink: 0

  &__nome
    flex: 1
</style>
